import { useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";

import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";

export const AssistantMessageContentSkeleton = ({ classNames }: { classNames?: string[] }) => (
  <>
    <Skeleton width="90%" className={cn("max-w-[180px]", classNames?.[0])} height={14} />
    <Skeleton width="70%" className={cn("max-w-[140px]", classNames?.[1])} height={14} />
  </>
);

export const MessageSkeleton = ({
  isUser = false,
  className,
}: {
  isUser?: boolean;
  className?: string;
}) => (
  <div
    className={cn(
      "flex items-end gap-2 px-0",
      "w-full",
      isUser ? "max-w-[60%] flex-row-reverse self-end" : "max-w-[85%] flex-row self-start",
      className,
    )}
  >
    <div className="relative flex w-full flex-col">
      <div
        className={cn(
          "relative flex flex-col gap-2 rounded-2xl px-3 py-2",
          "min-h-8",
          "rounded-bl-sm bg-gray-100",
        )}
      >
        <div className="flex flex-col gap-1.5">
          <Skeleton
            width={isUser ? "60%" : "100%"}
            className={isUser ? "max-w-[120px]" : "max-w-[200px]"}
            height={14}
          />
          {!isUser && <AssistantMessageContentSkeleton />}
        </div>
      </div>

      {!isUser && (
        <div className="mt-1 flex items-center gap-2 px-2">
          <Skeleton width="20%" className="max-w-[32px]" height={10} />
          <Skeleton width="30%" className="max-w-[48px]" height={10} />
          <Skeleton width="25%" className="max-w-[40px]" height={10} />
        </div>
      )}
    </div>
  </div>
);

/**
 * On the first page load in the global loading state, we show a skeleton for the chat panel.
 * @returns A skeleton for the chat panel
 */
export const PlaygroundChatPanelSkeleton = () => (
  <div className="flex flex-1 flex-col gap-4 overflow-hidden p-1">
    {Array.from({ length: 6 }, (_, index) => (
      <MessageSkeleton key={index} isUser={index % 2 === 0} />
    ))}
  </div>
);

export const LoadMoreMessagesSkeleton = () => {
  const skeletonMessages = useMemo(
    () =>
      Array.from({ length: 4 }).map((_, i) => ({
        id: `skeleton-${i}`,
        isUser: i % 2 === 0,
        delay: i * 0.07,
      })),
    [],
  );

  return (
    <>
      {skeletonMessages.map((skeleton, index) => (
        <AnimatePresence key={skeleton.id}>
          <motion.div
            initial={{ opacity: 0, scale: 0.8, transform: "translateY(-20px)" }}
            animate={{ opacity: 1, scale: 1, transform: "translateY(0)" }}
            transition={{ delay: skeleton.delay }}
            className="flex w-full flex-col gap-y-3"
          >
            <MessageSkeleton
              isUser={skeleton.isUser}
              className={cn("pb-3", index === skeletonMessages.length - 1 && "pb-0")}
            />
          </motion.div>
        </AnimatePresence>
      ))}
    </>
  );
};
